import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  useParams,
  Link,
  useBlocker,
  useSearchParams,
} from 'react-router-dom';
import { contentAPI } from '../services/api';
import DraftRevisionsModal from './DraftRevisionsModal';
import { useToast } from './Toast';
import { formatLabel, normalizeDocumentFormat } from '../constants/documentFormat';
import './ResearchBriefWorkspace.css';

const WORKSPACE_MODE_IDS = ['overview', 'research', 'outlining', 'writing'];

function normalizeDraftStatus(status) {
  if (status === 'approved' || status === 'published') return status;
  if (status === 'draft') return 'drafting';
  if (status === 'review') return 'reviewing';
  if (
    status === 'drafting' ||
    status === 'reviewing' ||
    status === 'needs_revisions'
  ) {
    return status;
  }
  return 'drafting';
}

function normalizeWorkspaceMode(raw) {
  const s =
    typeof raw === 'string'
      ? raw.trim().toLowerCase()
      : '';
  return WORKSPACE_MODE_IDS.includes(s) ? s : 'overview';
}

function googleSearchUrl(query) {
  const q = (query || '').trim();
  if (!q) return '#';
  return `https://www.google.com/search?q=${encodeURIComponent(q)}`;
}

/** Split queue `target_keyword` on commas; each phrase is one row / Google link. */
function splitQueueTargetKeywords(raw) {
  if (raw == null) return [];
  const s = String(raw).trim();
  if (!s) return [];
  const parts = s.split(',').map((p) => p.trim()).filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const p of parts) {
    const k = p.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(p);
  }
  return out;
}

function normalizeSection(s) {
  if (!s || typeof s !== 'object') {
    return { heading: '', key_points: [], subsections: [] };
  }
  const subs = Array.isArray(s.subsections)
    ? s.subsections.map((sub) => ({
        heading: typeof sub === 'string' ? sub : (sub.heading || ''),
        key_points: Array.isArray(sub.key_points) ? [...sub.key_points] : [],
      }))
    : [];
  return {
    heading: s.heading || '',
    key_points: Array.isArray(s.key_points) ? [...s.key_points] : [],
    subsections: subs,
  };
}

function normalizeOutline(raw) {
  if (raw == null) return [];
  let data = raw;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return [];
    }
  }
  if (Array.isArray(data)) return data.map(normalizeSection);
  if (data.main_sections && Array.isArray(data.main_sections)) {
    return data.main_sections.map(normalizeSection);
  }
  return [];
}

/** Drop UI-only keys before saving to the API. */
function outlineForApi(sections) {
  return sections.map(({ _sid, ...rest }) => ({ ...rest }));
}

/** Serialize outline JSON to a single markdown document for editing. */
function outlineToEditableMarkdown(sections) {
  const rows = [];
  for (const sec of outlineForApi(normalizeOutline(sections))) {
    const h = (sec.heading || '').trim();
    if (h) {
      rows.push(`## ${h}`, '');
    }
    for (const kp of (sec.key_points || []).map((k) => String(k).trim()).filter(Boolean)) {
      rows.push(`- ${kp}`);
    }
    const subs = sec.subsections || [];
    if (subs.length) {
      if (rows.length && rows[rows.length - 1] !== '') rows.push('');
      for (const sub of subs) {
        const sh = (sub.heading || '').trim();
        if (sh) {
          rows.push(`### ${sh}`, '');
        }
        for (const kp of (sub.key_points || [])
          .map((k) => String(k).trim())
          .filter(Boolean)) {
          rows.push(`- ${kp}`);
        }
        rows.push('');
      }
    }
    rows.push('');
  }
  return rows.join('\n').trim();
}

/**
 * Parse markdown with ## sections, ### subsections, and - / * list items into outline JSON.
 */
function markdownToOutline(markdown) {
  const lines = (markdown || '').split(/\r?\n/);
  const sections = [];
  let cur = null;
  let curSub = null;

  for (const raw of lines) {
    const t = raw.trim();
    if (!t) continue;
    if (t.startsWith('### ')) {
      if (!cur) {
        cur = { heading: '', key_points: [], subsections: [] };
        sections.push(cur);
      }
      curSub = { heading: t.slice(4).trim(), key_points: [] };
      cur.subsections.push(curSub);
    } else if (t.startsWith('## ')) {
      curSub = null;
      cur = { heading: t.slice(3).trim(), key_points: [], subsections: [] };
      sections.push(cur);
    } else if (/^[-*]\s+/.test(t)) {
      const item = t.replace(/^[-*]\s+/, '').trim();
      if (!cur) {
        cur = { heading: '', key_points: [], subsections: [] };
        sections.push(cur);
      }
      if (curSub) curSub.key_points.push(item);
      else cur.key_points.push(item);
    }
  }
  return sections.map(normalizeSection);
}

/** Preview rendered outline markup (React escapes text). */
function OutlineMarkdownPreview({ markdown }) {
  if (!(markdown || '').trim()) {
    return <p className="muted">Nothing to preview.</p>;
  }
  const lines = markdown.split(/\r?\n/);
  const blocks = [];
  const ulBucket = [];

  const flushUl = () => {
    if (!ulBucket.length) return;
    const items = ulBucket.splice(0);
    blocks.push(
      <ul className="rb-outline-preview-ul" key={`ul-${blocks.length}`}>
        {items.map((text, i) => (
          <li key={i}>{text}</li>
        ))}
      </ul>
    );
  };

  lines.forEach((raw, idx) => {
    const t = raw.trim();
    if (!t) {
      flushUl();
      return;
    }
    if (t.startsWith('### ')) {
      flushUl();
      blocks.push(
        <h4 className="rb-outline-preview-h4" key={`h-${idx}`}>
          {t.slice(4)}
        </h4>
      );
      return;
    }
    if (t.startsWith('## ') && !t.startsWith('###')) {
      flushUl();
      blocks.push(
        <h3 className="rb-outline-preview-h3" key={`h-${idx}`}>
          {t.slice(3)}
        </h3>
      );
      return;
    }
    if (/^[-*]\s+/.test(t)) {
      ulBucket.push(t.replace(/^[-*]\s+/, '').trim());
      return;
    }
    flushUl();
    blocks.push(
      <p className="rb-outline-preview-p" key={`p-${idx}`}>
        {t}
      </p>
    );
  });
  flushUl();
  return <div className="rb-outline-preview-inner">{blocks}</div>;
}

function escapeHtmlLite(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function safeArticleLinkHref(url) {
  const u = String(url).trim();
  if (/^https?:\/\//i.test(u) || /^mailto:/i.test(u)) {
    return escapeHtmlLite(u);
  }
  return '#';
}

function processArticleInlineMarkdown(text) {
  const div =
    typeof document !== 'undefined' ? document.createElement('div') : null;
  let html;
  if (div) {
    div.textContent = text;
    html = div.innerHTML;
  } else {
    html = escapeHtmlLite(text);
  }
  html = html.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    (_m, label, href) => {
      const lbl = escapeHtmlLite(String(label));
      const h = safeArticleLinkHref(href);
      return `<a href="${h}" target="_blank" rel="noopener noreferrer">${lbl}</a>`;
    }
  );
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');
  html = html.replace(/(^|[^*])\*([^*]+?)\*([^*]|$)/g, '$1<em>$2</em>$3');
  html = html.replace(/(^|[^_])_([^_]+?)_([^_]|$)/g, '$1<em>$2</em>$3');
  return html;
}

/** Article preview: headings, lists, paragraphs, basic inline Markdown (aligned with In Progress). */
function articleMarkdownToPreviewHtml(markdown) {
  if (!(markdown || '').trim()) {
    return '<p class="muted rb-article-preview-empty">Nothing to preview.</p>';
  }
  const lines = markdown.split('\n');
  const out = [];
  let inList = false;
  let listType = null;

  const closeList = () => {
    if (inList) {
      out.push(`</${listType}>`);
      inList = false;
      listType = null;
    }
  };

  for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
    const trimmed = lines[lineIdx].trim();

    if (trimmed.startsWith('### ')) {
      closeList();
      out.push(`<h3>${escapeHtmlLite(trimmed.slice(4))}</h3>`);
      continue;
    }
    if (trimmed.startsWith('## ') && !trimmed.startsWith('###')) {
      closeList();
      out.push(`<h2>${escapeHtmlLite(trimmed.slice(3))}</h2>`);
      continue;
    }
    if (trimmed.startsWith('# ') && !trimmed.startsWith('##')) {
      closeList();
      out.push(`<h1>${escapeHtmlLite(trimmed.slice(2))}</h1>`);
      continue;
    }

    if (trimmed.match(/^[-*] /)) {
      if (!inList || listType !== 'ul') {
        closeList();
        out.push('<ul>');
        inList = true;
        listType = 'ul';
      }
      const item = trimmed.replace(/^[-*]\s+/, '');
      out.push(`<li>${processArticleInlineMarkdown(item)}</li>`);
      continue;
    }

    if (trimmed.match(/^\d+\.\s+/)) {
      if (!inList || listType !== 'ol') {
        closeList();
        out.push('<ol>');
        inList = true;
        listType = 'ol';
      }
      const item = trimmed.replace(/^\d+\.\s+/, '');
      out.push(`<li>${processArticleInlineMarkdown(item)}</li>`);
      continue;
    }

    if (inList && trimmed !== '') {
      closeList();
    }

    if (trimmed === '') {
      out.push('');
    } else {
      out.push(`<p>${processArticleInlineMarkdown(trimmed)}</p>`);
    }
  }
  closeList();
  return out.join('\n');
}

function parseUrlDomain(url) {
  try {
    const u = new URL(url.trim());
    return u.hostname.replace(/^www\./i, '');
  } catch {
    return '';
  }
}

/** Same normalization as backend `_normalize_url_key` for rejected URL exclusion. */
function normalizeUrlKey(url) {
  try {
    const u = new URL(String(url).trim());
    const path = (u.pathname || '').replace(/\/$/, '') || '';
    return `${u.protocol}//${u.hostname.toLowerCase()}${path}`;
  } catch {
    return '';
  }
}

function newClaimId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? `cm_${crypto.randomUUID()}`
    : `cm_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/** Merge legacy global warnings[] onto claims by index when a claim has no per-claim warning. */
function claimsWithMergedWarnings(claimMap, globalWarnings) {
  const gw = globalWarnings || [];
  return (claimMap || []).map((c, i) => {
    const hasOwn =
      c.warning != null && String(c.warning).trim().length > 0;
    if (hasOwn) return c;
    const g = gw[i];
    if (g != null && String(g).trim()) return { ...c, warning: g };
    return c;
  });
}

/** Active alert for UI when claim is not approved (explicit warning or missing source). */
function claimAlertMessage(c) {
  if (c.approved) return '';
  const w = c.warning != null && String(c.warning).trim();
  if (w) return w;
  if (!c.source_id) {
    return 'No supporting source assigned. Choose one from your sources or approve this highlight if a citation is not required.';
  }
  return '';
}

function sourceOriginLabel(origin) {
  switch (origin) {
    case 'user_seed':
      return 'User seed';
    case 'user_added':
      return 'User added';
    case 'serp':
      return 'AI found';
    default:
      return origin || 'Web';
  }
}

function outlineToMarkdownHeadersOnly(sections) {
  const rows = [];
  for (const sec of outlineForApi(sections)) {
    const title = (sec.heading || '').trim();
    if (title) rows.push(`## ${title}`);
    for (const sub of sec.subsections || []) {
      const sh = (sub.heading || '').trim();
      if (sh) rows.push(`### ${sh}`);
    }
  }
  return rows.join('\n\n').trim();
}

/**
 * Headers plus bracketed writer prompts from key points (and subsection prompts).
 */
function outlineToMarkdownGuided(sections) {
  const parts = [];
  for (const sec of outlineForApi(sections)) {
    const title = (sec.heading || '').trim();
    if (!title) continue;
    parts.push(`## ${title}`);
    const kps = (sec.key_points || []).map((k) => k.trim()).filter(Boolean);
    const subs = sec.subsections || [];
    if (kps.length > 0) {
      parts.push('', kps.map((kp) => `[${kp}]`).join('\n\n'));
    } else if (subs.length === 0) {
      parts.push(
        '',
        `[Develop "${title}" using your executive summary, sources, and research highlights.]`
      );
    }
    for (const sub of subs) {
      const sh = (sub.heading || '').trim();
      if (!sh) continue;
      parts.push('', `### ${sh}`);
      const skp = (sub.key_points || []).map((k) => k.trim()).filter(Boolean);
      if (skp.length > 0) {
        parts.push('', skp.map((kp) => `[${kp}]`).join('\n\n'));
      } else {
        parts.push('', `[Expand this subsection with evidence from your sources.]`);
      }
    }
    parts.push('');
  }
  return parts.join('\n').trim();
}

/** Editable list of short text rows (add / edit / remove). */
function EditableStringList({ items, onChange, placeholder, addLabel }) {
  const update = (idx, val) => {
    const next = items.slice();
    next[idx] = val;
    onChange(next);
  };
  const remove = (idx) => {
    const next = items.slice();
    next.splice(idx, 1);
    onChange(next);
  };
  return (
    <div className="rb-angle-list">
      {items.map((item, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className="rb-angle-list-row" key={idx}>
          <input
            type="text"
            className="rb-angle-list-input"
            value={item}
            placeholder={placeholder}
            onChange={(e) => update(idx, e.target.value)}
          />
          <button
            type="button"
            className="rb-angle-list-remove"
            onClick={() => remove(idx)}
            aria-label="Remove item"
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn-secondary-sm rb-angle-list-add"
        onClick={() => onChange([...items, ''])}
      >
        {addLabel}
      </button>
    </div>
  );
}

function ResearchBriefWorkspace() {
  const { draftId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const workspaceMode = useMemo(
    () => normalizeWorkspaceMode(searchParams.get('mode')),
    [searchParams]
  );
  const setWorkspaceMode = useCallback(
    (next) => {
      const m = normalizeWorkspaceMode(next);
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (m === 'overview') p.delete('mode');
          else p.set('mode', m);
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const { showToast } = useToast();
  const articleRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [executiveSummary, setExecutiveSummary] = useState('');
  const [articleBody, setArticleBody] = useState('');
  const [outlineMarkdown, setOutlineMarkdown] = useState('');
  const [outlineViewMode, setOutlineViewMode] = useState('raw');
  const [articleViewMode, setArticleViewMode] = useState('raw');
  const [showAddSourceForm, setShowAddSourceForm] = useState(false);
  const [newSourceUrl, setNewSourceUrl] = useState('');
  const [newSourceTitle, setNewSourceTitle] = useState('');
  const [newSourceInfluence, setNewSourceInfluence] = useState('supporting');
  const [newSourceStatus, setNewSourceStatus] = useState('needs_review');
  const [addingSource, setAddingSource] = useState(false);
  const [sourceBusy, setSourceBusy] = useState(null);
  const [sourceModalId, setSourceModalId] = useState(null);
  const [claimModalId, setClaimModalId] = useState(null);
  const [claimModalBody, setClaimModalBody] = useState('');
  const [warningModalClaimId, setWarningModalClaimId] = useState(null);
  const [execSummaryCollapsed, setExecSummaryCollapsed] = useState(false);
  const [claimBusy, setClaimBusy] = useState(null);
  const [showAddClaimForm, setShowAddClaimForm] = useState(false);
  const [newClaimText, setNewClaimText] = useState('');
  const [newClaimSourceId, setNewClaimSourceId] = useState('');
  const [addingClaim, setAddingClaim] = useState(false);
  const [outlineResearchNeedsRefresh, setOutlineResearchNeedsRefresh] =
    useState(false);
  const [regeneratingOutline, setRegeneratingOutline] = useState(false);
  const [adjustDirectionOpen, setAdjustDirectionOpen] = useState(false);
  const [angleDraftPrimary, setAngleDraftPrimary] = useState('');
  const [angleDraftSupporting, setAngleDraftSupporting] = useState([]);
  const [angleDraftOutOfScope, setAngleDraftOutOfScope] = useState([]);
  const [execSummaryModalOpen, setExecSummaryModalOpen] = useState(false);
  const [execSummaryDraft, setExecSummaryDraft] = useState('');
  const [snapshotModalOpen, setSnapshotModalOpen] = useState(false);
  const [snapshotDescription, setSnapshotDescription] = useState('');
  const [snapshotIncludeResearch, setSnapshotIncludeResearch] = useState(false);
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [revisionsModalOpen, setRevisionsModalOpen] = useState(false);
  const [lastAutosaveAt, setLastAutosaveAt] = useState(null);
  const [sendingToPublish, setSendingToPublish] = useState(false);
  /** Writing-mode checklist only; sessionStorage keyed by draft (not persisted to API). */
  const [highlightUsedIds, setHighlightUsedIds] = useState({});

  /** Last saved server baseline for autosave dirty detection (article, summary, outline). */
  const saveBaselineRef = useRef({
    content: '',
    executive_summary: '',
    outline: [],
  });
  const [baselineTick, setBaselineTick] = useState(0);

  const syncSaveBaselineFromDraft = useCallback((d) => {
    saveBaselineRef.current = {
      content: d.content || '',
      executive_summary: d.executive_summary || '',
      outline: outlineForApi(normalizeOutline(d.outline)),
    };
    setBaselineTick((t) => t + 1);
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await contentAPI.getDraft(draftId);
      let d = res.data;
      const rb = { ...(d.research_brief || {}) };
      const cm = [...(rb.claim_map || [])];
      let claimsNeedIds = false;
      const nextClaims = cm.map((c) => {
        if (c.id) return c;
        claimsNeedIds = true;
        return { ...c, id: newClaimId() };
      });
      if (claimsNeedIds) {
        rb.claim_map = nextClaims;
        try {
          await contentAPI.updateDraft(draftId, { research_brief: rb });
          d = { ...d, research_brief: rb };
        } catch (e) {
          console.error(e);
          showToast(
            'Could not normalize research highlights — edit may fail until refresh.',
            'warning'
          );
        }
      }
      setDraft(d);
      setExecutiveSummary(d.executive_summary || '');
      setArticleBody(d.content || '');
      setOutlineMarkdown(outlineToEditableMarkdown(normalizeOutline(d.outline)));
      const rm = d.revision_metadata || {};
      const autosaveIso =
        rm.last_autosave_at || rm.last_manual_save_at || null;
      if (autosaveIso) setLastAutosaveAt(autosaveIso);
      syncSaveBaselineFromDraft(d);
    } catch (err) {
      console.error(err);
      showToast(
        `Failed to load draft: ${err.response?.data?.error || err.message}`,
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [draftId, showToast, syncSaveBaselineFromDraft]);

  const hasUnsavedEditorChanges = useMemo(() => {
    if (loading || !draft) return false;
    const b = saveBaselineRef.current;
    if (articleBody !== b.content) return true;
    if (executiveSummary !== b.executive_summary) return true;
    const curOutline = outlineForApi(markdownToOutline(outlineMarkdown));
    return JSON.stringify(curOutline) !== JSON.stringify(b.outline);
  }, [
    loading,
    draft,
    baselineTick,
    articleBody,
    executiveSummary,
    outlineMarkdown,
  ]);

  const researchGoogleQueries = useMemo(() => {
    if (!draft) return [];
    const phrases = splitQueueTargetKeywords(draft.target_keyword);
    return phrases.map((p) => ({ label: p, query: p }));
  }, [draft?.target_keyword]);

  /** Same-pathname navigations (e.g. ?mode=outlining) must not trigger leave prompts. */
  const shouldBlockLeave = useCallback(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedEditorChanges &&
      currentLocation.pathname !== nextLocation.pathname,
    [hasUnsavedEditorChanges]
  );

  const blocker = useBlocker(shouldBlockLeave);

  const draftRef = useRef(draft);
  draftRef.current = draft;
  const editorSnapshotRef = useRef({
    articleBody,
    executiveSummary,
    outlineMarkdown,
  });
  editorSnapshotRef.current = { articleBody, executiveSummary, outlineMarkdown };
  const persistInFlightRef = useRef(false);

  useEffect(() => {
    if (!hasUnsavedEditorChanges) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsavedEditorChanges]);

  useEffect(() => {
    if (blocker.state !== 'blocked') return undefined;
    const proceed = window.confirm(
      'You have unsaved changes to the article, executive summary, or outline. Leave this draft without saving?'
    );
    if (proceed) {
      const timer = window.setTimeout(() => blocker.proceed(), 0);
      return () => window.clearTimeout(timer);
    }
    blocker.reset();
    return undefined;
  }, [blocker]);

  useEffect(() => {
    if (blocker.state === 'blocked' && !hasUnsavedEditorChanges) {
      blocker.reset();
    }
  }, [blocker, hasUnsavedEditorChanges]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!draftId) return;
    try {
      const raw = sessionStorage.getItem(`rb-highlight-checks-${draftId}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          setHighlightUsedIds(parsed);
          return;
        }
      }
    } catch {
      /* ignore */
    }
    setHighlightUsedIds({});
  }, [draftId]);

  const toggleHighlightUsed = useCallback((id) => {
    setHighlightUsedIds((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try {
        if (draftId) {
          sessionStorage.setItem(
            `rb-highlight-checks-${draftId}`,
            JSON.stringify(next)
          );
        }
      } catch {
        /* ignore */
      }
      return next;
    });
  }, [draftId]);

  useEffect(() => {
    setOutlineResearchNeedsRefresh(false);
  }, [draftId]);

  useEffect(() => {
    const overlayOpen =
      sourceModalId ||
      claimModalId ||
      warningModalClaimId ||
      execSummaryModalOpen ||
      snapshotModalOpen;
    if (!overlayOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setSourceModalId(null);
        setClaimModalId(null);
        setWarningModalClaimId(null);
        setExecSummaryModalOpen(false);
        setSnapshotModalOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [sourceModalId, claimModalId, warningModalClaimId, execSummaryModalOpen, snapshotModalOpen]);

  const autosaveStatusLabel = useMemo(() => {
    if (hasUnsavedEditorChanges) return 'Unsaved changes';
    if (!lastAutosaveAt) return 'Saved';
    const sec = Math.floor((Date.now() - new Date(lastAutosaveAt).getTime()) / 1000);
    if (sec < 15) return 'Saved just now';
    if (sec < 3600) return `Saved ${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `Saved ${Math.floor(sec / 3600)}h ago`;
    return `Saved ${Math.floor(sec / 86400)}d ago`;
  }, [hasUnsavedEditorChanges, lastAutosaveAt]);

  const draftStatus = normalizeDraftStatus(draft?.status);
  const isInPublishingQueue = draftStatus === 'approved';
  const isPublished = draftStatus === 'published';

  const persistEditorState = useCallback(async () => {
    const outlineToSave = outlineForApi(markdownToOutline(outlineMarkdown));
    const nowIso = new Date().toISOString();
    persistInFlightRef.current = true;
    try {
      await contentAPI.updateDraft(draftId, {
        content: articleBody,
        executive_summary: executiveSummary,
        outline: outlineToSave,
        revision_metadata: {
          save_kind: 'autosave',
          last_autosave_at: nowIso,
        },
      });
      setLastAutosaveAt(nowIso);
      setDraft((prev) =>
        prev
          ? {
              ...prev,
              content: articleBody,
              executive_summary: executiveSummary,
              outline: outlineToSave,
            }
          : prev
      );
      syncSaveBaselineFromDraft({
        content: articleBody,
        executive_summary: executiveSummary,
        outline: outlineToSave,
      });
    } finally {
      persistInFlightRef.current = false;
    }
  }, [
    articleBody,
    draftId,
    executiveSummary,
    outlineMarkdown,
    syncSaveBaselineFromDraft,
  ]);

  const handleSendToPublishingQueue = async () => {
    if (isInPublishingQueue || isPublished) return;
    const bodyLen = (articleBody || '').trim().length;
    const warnShort =
      bodyLen > 0 && bodyLen < 80
        ? '\n\nNote: the article body is still very short — you can push to WordPress later once there is more content.'
        : bodyLen === 0
          ? '\n\nNote: the article body is empty — add content before pushing to WordPress.'
          : '';
    if (
      !window.confirm(
        `Send "${draft?.title || 'this draft'}" to the publishing queue?${warnShort}\n\nIt will appear under Publish → Publishing queue, where you can push to WordPress or mark published when live.`
      )
    ) {
      return;
    }
    try {
      setSendingToPublish(true);
      if (hasUnsavedEditorChanges) {
        await persistEditorState();
      }
      const res = await contentAPI.sendToPublishingQueue(draftId);
      setDraft((prev) =>
        prev
          ? {
              ...prev,
              status: 'approved',
              publish_preflight: res.data.publish_preflight ?? prev.publish_preflight,
              has_publish_preflight: res.data.has_publish_preflight,
            }
          : prev
      );
      if (res.data.generation_error) {
        showToast(
          res.data.message ||
            'Sent to publishing queue, but checklist generation failed — regenerate from the publish checklist page.',
          'warning',
          8000
        );
      } else {
        showToast(
          'Sent to publishing queue. Open the publish checklist when you are ready to copy into your CMS.',
          'success',
          7000
        );
      }
    } catch (err) {
      showToast(
        err.response?.data?.error || err.message || 'Could not send to publishing queue',
        'error'
      );
    } finally {
      setSendingToPublish(false);
    }
  };

  const handleConfirmSaveSnapshot = async () => {
    const outlineToSave = outlineForApi(markdownToOutline(outlineMarkdown));
    const payload = {
      content: articleBody,
      executive_summary: executiveSummary,
      outline: outlineToSave,
    };
    if (snapshotIncludeResearch && draft?.research_brief) {
      payload.research_brief = draft.research_brief;
    }
    try {
      setSavingSnapshot(true);
      persistInFlightRef.current = true;
      await contentAPI.updateDraft(draftId, {
        content: payload.content,
        executive_summary: payload.executive_summary,
        outline: payload.outline,
      });
      await contentAPI.createDraftSnapshot(draftId, {
        description: snapshotDescription.trim() || undefined,
        include_research_brief: snapshotIncludeResearch,
        payload,
      });
      showToast('Snapshot saved.', 'success');
      setSnapshotModalOpen(false);
      setSnapshotDescription('');
      setSnapshotIncludeResearch(false);
      await load();
    } catch (err) {
      showToast(
        `Snapshot failed: ${err.response?.data?.error || err.message}`,
        'error'
      );
    } finally {
      persistInFlightRef.current = false;
      setSavingSnapshot(false);
    }
  };

  /** Debounced autosave for article, summary, and outline (safety net; distinct from named snapshots). */
  useEffect(() => {
    if (loading || !draftId) return undefined;
    if (!hasUnsavedEditorChanges || savingSnapshot || refreshing) return undefined;

    const id = window.setTimeout(async () => {
      if (persistInFlightRef.current) return;
      const snap = editorSnapshotRef.current;
      const outlineToSave = outlineForApi(markdownToOutline(snap.outlineMarkdown));
      persistInFlightRef.current = true;
      try {
        const nowIso = new Date().toISOString();
        await contentAPI.updateDraft(draftId, {
          content: snap.articleBody,
          executive_summary: snap.executiveSummary,
          outline: outlineToSave,
          revision_metadata: {
            save_kind: 'autosave',
            last_autosave_at: nowIso,
          },
        });
        setLastAutosaveAt(nowIso);
        const after = editorSnapshotRef.current;
        const unchanged =
          after.articleBody === snap.articleBody &&
          after.executiveSummary === snap.executiveSummary &&
          after.outlineMarkdown === snap.outlineMarkdown;
        if (unchanged) {
          const base = draftRef.current;
          if (base) {
            const merged = {
              ...base,
              content: snap.articleBody,
              executive_summary: snap.executiveSummary,
              outline: outlineToSave,
            };
            setDraft(merged);
            syncSaveBaselineFromDraft(merged);
          }
        }
      } catch (err) {
        console.error(err);
        showToast(
          `Autosave failed: ${err.response?.data?.error || err.message}`,
          'error'
        );
      } finally {
        persistInFlightRef.current = false;
      }
    }, 2000);

    return () => window.clearTimeout(id);
  }, [
    articleBody,
    executiveSummary,
    outlineMarkdown,
    draftId,
    loading,
    hasUnsavedEditorChanges,
    savingSnapshot,
    refreshing,
    syncSaveBaselineFromDraft,
    showToast,
  ]);

  const handleRegenerateOutlineFromResearch = async () => {
    if (!draft || draft.workflow_kind !== 'human_research') return;
    if (
      !window.confirm(
        'Regenerate the proposed outline from your current sources and research highlights? ' +
          'The new outline is saved to the draft immediately; article text and executive summary are not changed.'
      )
    ) {
      return;
    }
    try {
      setRegeneratingOutline(true);
      await contentAPI.regenerateOutlineFromResearch(draftId);
      setOutlineResearchNeedsRefresh(false);
      showToast('Outline regenerated from sources and research highlights.', 'success');
      await load();
    } catch (err) {
      showToast(
        err.response?.data?.error || err.message || 'Outline regeneration failed',
        'error'
      );
    } finally {
      setRegeneratingOutline(false);
    }
  };

  const openAdjustDirection = () => {
    const meta = draft?.research_brief?.meta || {};
    setAngleDraftPrimary(meta.article_angle || '');
    setAngleDraftSupporting(
      Array.isArray(meta.supporting_points) ? [...meta.supporting_points] : []
    );
    setAngleDraftOutOfScope(
      Array.isArray(meta.out_of_scope) ? [...meta.out_of_scope] : []
    );
    setAdjustDirectionOpen(true);
  };

  const handleSaveDirectionAndRegen = async () => {
    const primary = angleDraftPrimary.trim();
    const supporting = angleDraftSupporting.map((s) => s.trim()).filter(Boolean);
    const outOfScope = angleDraftOutOfScope.map((s) => s.trim()).filter(Boolean);
    if (!primary && supporting.length === 0 && outOfScope.length === 0) {
      showToast(
        'Add a primary angle or at least one point before regenerating.',
        'warning'
      );
      return;
    }
    try {
      setRegeneratingOutline(true);
      await contentAPI.regenerateOutlineFromResearch(draftId, {
        article_angle: {
          primary_angle: primary,
          supporting_points: supporting,
          out_of_scope: outOfScope,
        },
      });
      setOutlineResearchNeedsRefresh(false);
      setAdjustDirectionOpen(false);
      showToast('Article direction updated and outline regenerated.', 'success');
      await load();
    } catch (err) {
      showToast(
        err.response?.data?.error || err.message || 'Outline regeneration failed',
        'error'
      );
    } finally {
      setRegeneratingOutline(false);
    }
  };

  const handleRefreshPacket = async () => {
    if (
      !window.confirm(
        'Regenerate the research packet from live search + AI? Your article text stays; outline and sources will update.'
      )
    ) {
      return;
    }
    try {
      setRefreshing(true);
      await contentAPI.refreshResearchBrief(draftId);
      setOutlineResearchNeedsRefresh(false);
      showToast('Research brief refreshed.', 'success');
      await load();
    } catch (err) {
      showToast(
        err.response?.data?.error || err.message || 'Refresh failed',
        'error'
      );
    } finally {
      setRefreshing(false);
    }
  };

  const insertOutlineMarkdown = useCallback(
    (mode) => {
      const sections = markdownToOutline(outlineMarkdown);
      if (!sections.length) {
        showToast('No outline sections to insert.', 'warning');
        return;
      }
      const md =
        mode === 'headers'
          ? outlineToMarkdownHeadersOnly(sections)
          : outlineToMarkdownGuided(sections);
      if (!md) {
        showToast('Nothing to insert — add section headings to your outline.', 'warning');
        return;
      }

      const el = articleRef.current;
      const canUseCaret =
        el && document.activeElement === el && typeof el.selectionStart === 'number';
      const selStart = canUseCaret ? el.selectionStart : null;
      const selEnd = canUseCaret ? el.selectionEnd : null;

      const prefix = selStart !== null && selStart > 0 ? '\n\n' : '';
      const block = prefix + md + '\n\n';
      const nextCaret =
        selStart !== null ? selStart + block.length : null;

      setArticleBody((prev) => {
        if (selStart !== null && selEnd !== null) {
          return prev.slice(0, selStart) + block + prev.slice(selEnd);
        }
        return prev.trim() ? `${prev.trimEnd()}\n\n${md}\n\n` : `${md}\n\n`;
      });

      queueMicrotask(() => {
        const ta = articleRef.current;
        if (!ta) return;
        ta.focus();
        if (nextCaret !== null) {
          ta.setSelectionRange(nextCaret, nextCaret);
        } else {
          const len = ta.value.length;
          ta.setSelectionRange(len, len);
        }
      });

      showToast(
        mode === 'headers'
          ? 'Headers inserted into the article.'
          : 'Guided draft placeholders inserted into the article.',
        'success'
      );
    },
    [outlineMarkdown, showToast]
  );

  const handleAddManualSource = async () => {
    const url = newSourceUrl.trim();
    if (!url) {
      showToast('URL is required.', 'warning');
      return;
    }
    try {
      new URL(url);
    } catch {
      showToast('Enter a valid http(s) URL.', 'error');
      return;
    }
    try {
      setAddingSource(true);
      const domain = parseUrlDomain(url);
      const id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? `manual_${crypto.randomUUID()}`
          : `manual_${Date.now()}`;
      const entry = {
        id,
        url,
        title: newSourceTitle.trim() || domain || 'Untitled',
        snippet: '',
        publisher_domain: domain,
        retrieved_at: new Date().toISOString(),
        claim_strength: 'reference',
        origin: 'user_added',
        influence: (newSourceInfluence || 'supporting').trim() || 'supporting',
        review_status: newSourceStatus || 'needs_review',
      };
      const rbNext = { ...(draft.research_brief || {}) };
      rbNext.source_links = [...(rbNext.source_links || []), entry];
      await contentAPI.updateDraft(draftId, { research_brief: rbNext });
      setDraft((prev) => (prev ? { ...prev, research_brief: rbNext } : prev));
      setOutlineResearchNeedsRefresh(true);
      setNewSourceUrl('');
      setNewSourceTitle('');
      setNewSourceInfluence('supporting');
      setNewSourceStatus('needs_review');
      setShowAddSourceForm(false);
      showToast('Manual source added.', 'success');
    } catch (err) {
      showToast(
        err.response?.data?.error || err.message || 'Could not add source',
        'error'
      );
    } finally {
      setAddingSource(false);
    }
  };

  const updateSourceFields = useCallback(
    async (sourceId, patch) => {
      if (!draft) return;
      try {
        setSourceBusy(sourceId);
        const rb0 = { ...(draft.research_brief || {}) };
        rb0.source_links = (rb0.source_links || []).map((x) =>
          x.id === sourceId ? { ...x, ...patch } : x
        );
        await contentAPI.updateDraft(draftId, { research_brief: rb0 });
        setDraft((prev) => (prev ? { ...prev, research_brief: rb0 } : prev));
        setOutlineResearchNeedsRefresh(true);
      } catch (err) {
        showToast(
          err.response?.data?.error || err.message || 'Could not update source',
          'error'
        );
      } finally {
        setSourceBusy(null);
      }
    },
    [draft, draftId, showToast]
  );

  const updateClaimFields = useCallback(
    async (claimId, patch) => {
      if (!draft) return;
      try {
        setClaimBusy(claimId);
        const rb0 = { ...(draft.research_brief || {}) };
        rb0.claim_map = (rb0.claim_map || []).map((x) =>
          x.id === claimId ? { ...x, ...patch } : x
        );
        await contentAPI.updateDraft(draftId, { research_brief: rb0 });
        setDraft((prev) => (prev ? { ...prev, research_brief: rb0 } : prev));
        setOutlineResearchNeedsRefresh(true);
      } catch (err) {
        showToast(
          err.response?.data?.error || err.message || 'Could not update highlight',
          'error'
        );
      } finally {
        setClaimBusy(null);
      }
    },
    [draft, draftId, showToast]
  );

  const removeClaim = useCallback(
    async (claimId) => {
      if (!draft) return;
      try {
        setClaimBusy(claimId);
        const rb0 = { ...(draft.research_brief || {}) };
        rb0.claim_map = (rb0.claim_map || []).filter((x) => x.id !== claimId);
        await contentAPI.updateDraft(draftId, { research_brief: rb0 });
        setDraft((prev) => (prev ? { ...prev, research_brief: rb0 } : prev));
        setOutlineResearchNeedsRefresh(true);
        setClaimModalId((mid) => (mid === claimId ? null : mid));
        setWarningModalClaimId((wid) => (wid === claimId ? null : wid));
        showToast('Research highlight removed.', 'success');
      } catch (err) {
        showToast(
          err.response?.data?.error || err.message || 'Could not remove highlight',
          'error'
        );
      } finally {
        setClaimBusy(null);
      }
    },
    [draft, draftId, showToast]
  );

  const handleAddClaim = async () => {
    const text = newClaimText.trim();
    if (!text) {
      showToast('Enter a talking point or highlight.', 'warning');
      return;
    }
    if (!draft) return;
    try {
      setAddingClaim(true);
      const rbNext = { ...(draft.research_brief || {}) };
      const sid = newClaimSourceId.trim();
      const entry = {
        id: newClaimId(),
        claim: text,
        source_id: sid || null,
        claim_strength: 'opinion',
        note: '',
        origin: 'user_added',
      };
      rbNext.claim_map = [...(rbNext.claim_map || []), entry];
      await contentAPI.updateDraft(draftId, { research_brief: rbNext });
      setDraft((prev) => (prev ? { ...prev, research_brief: rbNext } : prev));
      setOutlineResearchNeedsRefresh(true);
      setNewClaimText('');
      setNewClaimSourceId('');
      setShowAddClaimForm(false);
      showToast('Research highlight added.', 'success');
    } catch (err) {
      showToast(
        err.response?.data?.error || err.message || 'Could not add highlight',
        'error'
      );
    } finally {
      setAddingClaim(false);
    }
  };

  const rejectSource = useCallback(
    async (s) => {
      if (!draft) return;
      const key = normalizeUrlKey(s.url);
      if (!key) {
        showToast('Could not use this URL for exclusion.', 'warning');
        return;
      }
      try {
        setSourceBusy(s.id);
        const rb0 = { ...(draft.research_brief || {}) };
        rb0.source_links = (rb0.source_links || []).filter((x) => x.id !== s.id);
        const rejSet = new Set(
          (rb0.rejected_urls || []).map((u) => normalizeUrlKey(u)).filter(Boolean)
        );
        rejSet.add(key);
        rb0.rejected_urls = Array.from(rejSet);
        await contentAPI.updateDraft(draftId, { research_brief: rb0 });
        setDraft((prev) => (prev ? { ...prev, research_brief: rb0 } : prev));
        setOutlineResearchNeedsRefresh(true);
        setSourceModalId((mid) => (mid === s.id ? null : mid));
        showToast(
          'Source removed. It stays excluded when you refresh the research packet.',
          'success'
        );
      } catch (err) {
        showToast(
          err.response?.data?.error || err.message || 'Could not remove source',
          'error'
        );
      } finally {
        setSourceBusy(null);
      }
    },
    [draft, draftId, showToast]
  );

  if (loading) {
    return <div className="loading">Loading research workspace…</div>;
  }
  if (!draft) {
    return (
      <div className="research-workspace empty">
        <p>Draft not found.</p>
        <Link to="/content-queue">Back to queue</Link>
      </div>
    );
  }

  const rb = draft.research_brief || {};
  const sources = rb.source_links || [];
  const modalSource = sourceModalId
    ? sources.find((x) => x.id === sourceModalId) || null
    : null;
  const claims = claimsWithMergedWarnings(rb.claim_map, rb.warnings);
  const modalClaim = claimModalId
    ? claims.find((x) => x.id === claimModalId) || null
    : null;
  const warningModalClaim = warningModalClaimId
    ? claims.find((x) => x.id === warningModalClaimId) || null
    : null;
  const isHuman = draft.workflow_kind === 'human_research';
  const targetWordCount = draft?.research_brief?.meta?.target_word_count;
  const documentFormat = normalizeDocumentFormat(
    draft?.research_brief?.meta?.document_format
  );

  const lockedAngle = rb.meta || {};
  const lockedSupporting = Array.isArray(lockedAngle.supporting_points)
    ? lockedAngle.supporting_points
    : [];
  const lockedOutOfScope = Array.isArray(lockedAngle.out_of_scope)
    ? lockedAngle.out_of_scope
    : [];
  const hasLockedAngle = !!(
    (lockedAngle.article_angle || '').trim() ||
    lockedSupporting.length > 0 ||
    lockedOutOfScope.length > 0
  );

  const angleResteerCard = (
    <section className="rb-card rb-angle-card">
      <div className="rb-card-heading-row">
        <h3>Article direction</h3>
        {!adjustDirectionOpen && (
          <button
            type="button"
            className="btn-secondary-sm"
            onClick={openAdjustDirection}
            disabled={regeneratingOutline || refreshing || loading}
          >
            {hasLockedAngle ? 'Adjust article direction' : 'Set article direction'}
          </button>
        )}
      </div>

      {!adjustDirectionOpen ? (
        <>
          <p className="hint sm rb-angle-hint">
            The locked direction steers outline regeneration and keeps excluded topics out.
          </p>
          {hasLockedAngle ? (
            <div className="rb-angle-summary">
              {(lockedAngle.article_angle || '').trim() && (
                <p className="rb-angle-primary">{lockedAngle.article_angle}</p>
              )}
              {lockedSupporting.length > 0 && (
                <>
                  <p className="rb-angle-label">Supporting points</p>
                  <ul className="rb-angle-summary-list">
                    {lockedSupporting.map((p, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={`sp-${i}`}>{p}</li>
                    ))}
                  </ul>
                </>
              )}
              {lockedOutOfScope.length > 0 && (
                <>
                  <p className="rb-angle-label">Out of scope</p>
                  <ul className="rb-angle-summary-list rb-angle-summary-list--exclude">
                    {lockedOutOfScope.map((p, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={`oos-${i}`}>{p}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          ) : (
            <p className="muted sm">
              No locked direction yet. Set one to steer the outline if it drifts off topic.
            </p>
          )}
        </>
      ) : (
        <div className="rb-angle-editor">
          <label className="rb-angle-field-label" htmlFor="rb-angle-primary">
            Primary angle
          </label>
          <textarea
            id="rb-angle-primary"
            className="rb-textarea rb-angle-textarea"
            value={angleDraftPrimary}
            onChange={(e) => setAngleDraftPrimary(e.target.value)}
            rows={3}
            placeholder="The single main angle this article should take"
          />
          <label className="rb-angle-field-label">Supporting points</label>
          <EditableStringList
            items={angleDraftSupporting}
            onChange={setAngleDraftSupporting}
            placeholder="A supporting point that stays on the primary angle"
            addLabel="+ Add supporting point"
          />
          <label className="rb-angle-field-label">Out of scope</label>
          <EditableStringList
            items={angleDraftOutOfScope}
            onChange={setAngleDraftOutOfScope}
            placeholder="e.g., Beam span / horizontal load tables"
            addLabel="+ Add out-of-scope topic"
          />
          <p className="hint sm">
            Saving updates the locked direction and regenerates the outline only — sources are not
            re-fetched.
          </p>
          <div className="rb-angle-editor-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setAdjustDirectionOpen(false)}
              disabled={regeneratingOutline}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => void handleSaveDirectionAndRegen()}
              disabled={regeneratingOutline}
            >
              {regeneratingOutline ? 'Regenerating…' : 'Save & regenerate outline'}
            </button>
          </div>
        </div>
      )}
    </section>
  );

  const outlineSection = (
    <section className="rb-card rb-outline-card rb-outline-stretch">
      <div className="rb-card-heading-row">
        <h3>Proposed outline</h3>
        <div className="rb-toggle-group">
          <button
            type="button"
            className={outlineViewMode === 'raw' ? 'active' : ''}
            onClick={() => setOutlineViewMode('raw')}
          >
            Raw
          </button>
          <button
            type="button"
            className={outlineViewMode === 'preview' ? 'active' : ''}
            onClick={() => setOutlineViewMode('preview')}
          >
            Preview
          </button>
        </div>
      </div>
      {isHuman && outlineResearchNeedsRefresh && (
        <div className="rb-outline-stale-banner" role="status">
          <p className="rb-outline-stale-text">
            Sources or research highlights changed — your outline may no longer match. Consider
            regenerating it from the current research packet.
          </p>
          <button
            type="button"
            className="btn-primary-sm rb-outline-regen-btn"
            onClick={() => void handleRegenerateOutlineFromResearch()}
            disabled={
              regeneratingOutline || refreshing || savingSnapshot || loading
            }
          >
            {regeneratingOutline
              ? 'Regenerating…'
              : 'Regenerate outline from sources & highlights'}
          </button>
        </div>
      )}
      <p className="hint rb-outline-hint">
        Use Markdown: <code className="rb-code-hint">##</code> section headings,{' '}
        <code className="rb-code-hint">###</code> subsections, and{' '}
        <code className="rb-code-hint">-</code> bullets for key points. Inserts go into the article at
        the caret (or the end if the article is not focused).
      </p>
      <div className="rb-outline-insert-row">
        <button
          type="button"
          className="rb-btn-insert"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => insertOutlineMarkdown('headers')}
        >
          Insert Headers Only
        </button>
        <button
          type="button"
          className="rb-btn-insert rb-btn-insert-primary"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => insertOutlineMarkdown('guided')}
        >
          Insert Guided Draft
        </button>
      </div>
      <div className="rb-outline-editor-column">
        {outlineViewMode === 'raw' ? (
          <textarea
            className="rb-textarea rb-outline-markdown mono"
            value={outlineMarkdown}
            onChange={(e) => setOutlineMarkdown(e.target.value)}
            spellCheck={false}
            placeholder={`## Introduction\n\n- Hook the reader\n- State the thesis\n\n### Background\n\n- Context bullets`}
          />
        ) : (
          <div className="rb-outline-preview-pane">
            <OutlineMarkdownPreview markdown={outlineMarkdown} />
          </div>
        )}
      </div>
    </section>
  );

  const articleSection = (
    <section className="rb-card article-card rb-article-stretch">
      <div className="rb-card-heading-row">
        <h3>Article (your writing)</h3>
        <div className="rb-toggle-group">
          <button
            type="button"
            className={articleViewMode === 'raw' ? 'active' : ''}
            onClick={() => setArticleViewMode('raw')}
          >
            Raw
          </button>
          <button
            type="button"
            className={articleViewMode === 'preview' ? 'active' : ''}
            onClick={() => setArticleViewMode('preview')}
          >
            Preview
          </button>
        </div>
      </div>
      <div className="rb-article-body-column">
        {articleViewMode === 'raw' ? (
          <textarea
            ref={articleRef}
            className="rb-textarea article"
            value={articleBody}
            onChange={(e) => setArticleBody(e.target.value)}
            placeholder="Write your article in Markdown…"
          />
        ) : (
          <div
            className="rb-article-preview-pane"
            dangerouslySetInnerHTML={{
              __html: articleMarkdownToPreviewHtml(articleBody),
            }}
          />
        )}
      </div>
    </section>
  );

  const sourceNumById = new Map();
  sources.forEach((s, i) => {
    if (s.id) sourceNumById.set(s.id, i + 1);
  });

  const sortedClaimsForWritingChecklist = (() => {
    const unchecked = claims.filter((c) => !highlightUsedIds[c.id]);
    const checked = claims.filter((c) => highlightUsedIds[c.id]);
    return [...unchecked, ...checked];
  })();

  const sourcesSection = (
    <section className="rb-card rb-pane rb-pane-wide">
      <div className="rb-sources-header">
        <h3>Sources</h3>
        <button
          type="button"
          className="rb-btn-add-source-toggle"
          onClick={() => setShowAddSourceForm((v) => !v)}
        >
          {showAddSourceForm ? 'Close' : '+ Add manual source'}
        </button>
      </div>
      {showAddSourceForm && (
        <div className="rb-add-source-form">
          <p className="hint sm">
            Editorial links you add while writing (not re-run through the research pass unless
            you refresh the packet; user-added rows are preserved on refresh).
          </p>
          <label className="rb-mini-label">URL</label>
          <input
            type="url"
            className="rb-input"
            value={newSourceUrl}
            onChange={(e) => setNewSourceUrl(e.target.value)}
            placeholder="https://…"
          />
          <label className="rb-mini-label">Title (optional)</label>
          <input
            type="text"
            className="rb-input"
            value={newSourceTitle}
            onChange={(e) => setNewSourceTitle(e.target.value)}
            placeholder="Defaults to domain if empty"
          />
          <div className="rb-add-source-row">
            <div>
              <label className="rb-mini-label">Source role</label>
              <select
                className="rb-select"
                value={newSourceInfluence || 'supporting'}
                onChange={(e) => setNewSourceInfluence(e.target.value)}
              >
                <option value="preferred">Preferred</option>
                <option value="supporting">Supporting</option>
                <option value="counterpoint">Counterpoint</option>
                <option value="statistical_authority">Statistical authority</option>
                <option value="context_only">Context only</option>
              </select>
            </div>
            <div>
              <label className="rb-mini-label">Review</label>
              <select
                className="rb-select"
                value={newSourceStatus}
                onChange={(e) => setNewSourceStatus(e.target.value)}
              >
                <option value="needs_review">Needs review</option>
                <option value="verified">Verified</option>
              </select>
            </div>
          </div>
          <button
            type="button"
            className="btn-primary-sm"
            onClick={handleAddManualSource}
            disabled={addingSource}
          >
            {addingSource ? 'Adding…' : 'Add to list'}
          </button>
        </div>
      )}
      <div className="rb-pane-body rb-pane-body-wide">
        {sources.length === 0 ? (
          <p className="muted sm">
            None captured — add seeds when creating the brief, refresh for Serp results, or add
            manual sources above.
          </p>
        ) : (
          <ul className="sources-list sources-list-wide">
            {sources.map((s) => (
              <li key={s.id || s.url} className="rb-source-row">
                <button
                  type="button"
                  className="rb-source-corner-btn"
                  title="Source role, review, reject…"
                  aria-label="Edit source options"
                  aria-haspopup="dialog"
                  aria-expanded={sourceModalId === s.id}
                  onClick={() => setSourceModalId(s.id)}
                >
                  ⋯
                </button>
                <div className="rb-source-badges">
                  <span
                    className={`src-pill origin-${s.origin || 'serp'}`}
                    title="Provenance"
                  >
                    {sourceOriginLabel(s.origin || 'serp')}
                  </span>
                  <span className="src-pill influence sm">
                    {(s.influence || 'supporting').replace(/_/g, ' ')}
                  </span>
                  {s.review_status === 'verified' && (
                    <span className="src-pill verified sm">Verified</span>
                  )}
                  {s.review_status === 'needs_review' && (
                    <span className="src-pill review sm">Review</span>
                  )}
                </div>
                <a href={s.url} target="_blank" rel="noopener noreferrer">
                  {s.title || s.url}
                </a>
                <span className="src-meta">
                  {s.publisher_domain}
                  {s.retrieved_at && ` · ${new Date(s.retrieved_at).toLocaleDateString()}`}
                </span>
                {s.snippet && <p className="snippet snippet-clamp">{s.snippet}</p>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );

  const researchHighlightsSection = (
    <section className="rb-card rb-pane rb-pane-wide">
      <div className="rb-sources-header">
        <h3>Research Highlights</h3>
        <button
          type="button"
          className="rb-btn-add-source-toggle"
          onClick={() => setShowAddClaimForm((v) => !v)}
        >
          {showAddClaimForm ? 'Close' : 'Add highlight'}
        </button>
      </div>
      {showAddClaimForm && (
        <div className="rb-add-source-form rb-add-claim-form">
          <p className="hint sm">
            Add an angle or point you want to make. Optionally tie it to a source from your list
            (add sources above first if needed).
          </p>
          <label className="rb-mini-label" htmlFor="rb-new-claim-text">
            Talking point or highlight
          </label>
          <textarea
            id="rb-new-claim-text"
            className="rb-textarea"
            rows={3}
            value={newClaimText}
            onChange={(e) => setNewClaimText(e.target.value)}
            placeholder="e.g. Industry surveys show X grew 12% YoY…"
          />
          <label className="rb-mini-label" htmlFor="rb-new-claim-src">
            Supporting source (optional)
          </label>
          <select
            id="rb-new-claim-src"
            className="rb-select"
            value={newClaimSourceId}
            onChange={(e) => setNewClaimSourceId(e.target.value)}
          >
            <option value="">None — assign later</option>
            {sources.map((s) => (
              <option key={s.id || s.url} value={s.id || ''}>
                {(s.title || s.url || '').slice(0, 72)}
                {s.id ? ` (${s.id})` : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn-primary-sm"
            onClick={() => void handleAddClaim()}
            disabled={addingClaim}
          >
            {addingClaim ? 'Adding…' : 'Add to list'}
          </button>
        </div>
      )}
      <div className="rb-pane-body rb-pane-body-wide">
        {claims.length === 0 ? (
          <p className="muted sm">No research highlights yet.</p>
        ) : (
          <ul className="claims-list claims-list-wide">
            {claims.map((c) => {
              const alertMsg = claimAlertMessage(c);
              const approved = !!c.approved;
              return (
                <li key={c.id} className="rb-claim-row">
                  <button
                    type="button"
                    className="rb-claim-corner-btn"
                    title="Edit highlight, assign source, approve…"
                    aria-label="Research highlight options"
                    aria-haspopup="dialog"
                    aria-expanded={claimModalId === c.id}
                    onClick={() => {
                      setClaimModalId(c.id);
                      setClaimModalBody(c.claim || '');
                    }}
                  >
                    ⋯
                  </button>
                  <div className="rb-claim-head">
                    {alertMsg ? (
                      <button
                        type="button"
                        className="rb-claim-warn-icon"
                        title="Open warning"
                        aria-label="Highlight warning"
                        onClick={() => setWarningModalClaimId(c.id)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 8v4M12 16h.01" strokeLinecap="round" />
                        </svg>
                      </button>
                    ) : (
                      <span className="rb-claim-warn-spacer" aria-hidden />
                    )}
                    <div
                      className={`claim-text ${approved ? 'claim-approved' : ''}`}
                    >
                      {c.claim}
                    </div>
                  </div>
                  <div className="claim-meta">
                    {approved && (
                      <span className="badge badge-approved">Approved</span>
                    )}
                    {c.claim_strength && (
                      <span className="badge">{c.claim_strength}</span>
                    )}
                    {c.source_id && (
                      <span className="src-id">→ {c.source_id}</span>
                    )}
                    {!c.source_id && !approved && (
                      <span className="warn-tag">needs source</span>
                    )}
                  </div>
                  {c.note && <p className="note">{c.note}</p>}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );

  return (
    <div className={`research-workspace rb-mode-${workspaceMode}`}>
      <div className="rb-header">
        <div>
          <h2>Draft research workspace</h2>
          <p className="rb-sub">
            AI-assisted research for <strong>human-written</strong> content. Cite
            sources from the list; edit summary and outline as you refine the piece.
          </p>
          {documentFormat !== 'blog' && (
            <span className="rb-document-format-badge">
              {formatLabel(documentFormat)}
            </span>
          )}
          {targetWordCount != null && (
            <span className="rb-target-word-count">
              Target: ~{Number(targetWordCount).toLocaleString()} words
            </span>
          )}
        </div>
        <div className="rb-header-actions">
          <span
            className={`rb-save-status ${hasUnsavedEditorChanges ? 'rb-save-status--dirty' : ''}`}
            aria-live="polite"
          >
            {autosaveStatusLabel}
          </span>
          {isPublished ? (
              <>
                <span className="rb-workflow-badge rb-workflow-badge--published">
                  Published
                </span>
                <Link
                  to={`/publish-preflight/${draftId}`}
                  className="btn-secondary"
                >
                  Open publish checklist
                </Link>
                <Link to="/published" className="btn-secondary">
                  Published articles
                </Link>
              </>
            ) : isInPublishingQueue ? (
              <>
                <span className="rb-workflow-badge rb-workflow-badge--queue">
                  In publishing queue
                </span>
                <Link
                  to={`/publish-preflight/${draftId}`}
                  className="rb-btn-send-publish"
                >
                  Open publish checklist
                </Link>
                <Link to="/publishing-queue" className="btn-secondary">
                  Open publishing queue
                </Link>
              </>
            ) : (
              <button
                type="button"
                className="rb-btn-send-publish"
                onClick={() => void handleSendToPublishingQueue()}
                disabled={sendingToPublish || !draft}
              >
                {sendingToPublish ? 'Sending…' : 'Send to publishing queue'}
              </button>
            )}
          <Link to="/research-briefs" className="btn-secondary">
            Research briefs
          </Link>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setRevisionsModalOpen(true)}
          >
            Revisions
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setSnapshotModalOpen(true)}
            disabled={savingSnapshot}
          >
            {savingSnapshot ? 'Saving…' : 'Save snapshot'}
          </button>
          {isHuman && (
            <button
              type="button"
              className="btn-refresh"
              onClick={handleRefreshPacket}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing…' : 'Refresh research packet'}
            </button>
          )}
        </div>
      </div>

      <nav className="rb-mode-bar" aria-label="Workspace mode">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'research', label: 'Research' },
          { id: 'outlining', label: 'Outlining' },
          { id: 'writing', label: 'Writing' },
        ].map((m) => (
          <button
            key={m.id}
            type="button"
            className={`rb-mode-bar-btn ${workspaceMode === m.id ? 'active' : ''}`}
            aria-current={workspaceMode === m.id ? 'true' : undefined}
            onClick={() => setWorkspaceMode(m.id)}
          >
            {m.label}
          </button>
        ))}
      </nav>

      {isInPublishingQueue && !isPublished && (
        <div className="rb-publishing-banner" role="status">
          <p>
            This draft is ready to publish. Use the{' '}
            <strong>publish checklist</strong> beside your CMS, or open the publishing queue to push
            to WordPress.
          </p>
          <Link
            to={`/publish-preflight/${draftId}`}
            className="rb-btn-send-publish rb-btn-send-publish--sm"
          >
            Open publish checklist
          </Link>
          <Link to="/publishing-queue" className="btn-secondary">
            Publishing queue
          </Link>
        </div>
      )}

      {!isHuman && (
        <div className="rb-banner legacy">
          This draft was created with the <strong>legacy full AI article</strong>{' '}
          generator. You can still edit here; research panels may be empty.
        </div>
      )}

      <section
        className={`rb-card rb-exec-summary-fullwidth${execSummaryCollapsed ? ' rb-exec-collapsed' : ''}`}
      >
        <div className="rb-exec-summary-header-row">
          <h3>Executive summary</h3>
          <div className="rb-exec-summary-actions">
            <button
              type="button"
              className="btn-secondary-sm rb-exec-collapse-btn"
              onClick={() => setExecSummaryCollapsed((v) => !v)}
              aria-expanded={!execSummaryCollapsed}
            >
              {execSummaryCollapsed ? 'Expand summary' : 'Collapse'}
            </button>
            <button
              type="button"
              className="rb-exec-summary-corner-btn"
              title="Edit executive summary"
              aria-label="Edit executive summary"
              aria-haspopup="dialog"
              aria-expanded={execSummaryModalOpen}
              onClick={() => {
                setExecSummaryDraft(executiveSummary);
                setExecSummaryModalOpen(true);
              }}
            >
              ⋯
            </button>
          </div>
        </div>
        {!execSummaryCollapsed && (
          <div className="rb-exec-summary-body">
            {executiveSummary.trim() ? (
              <div className="rb-exec-summary-display">{executiveSummary}</div>
            ) : (
              <p className="muted rb-exec-summary-empty">
                No summary yet — open the menu to add one. Changes autosave; use <strong>Save snapshot</strong> for named revisions.
              </p>
            )}
          </div>
        )}
      </section>

      {workspaceMode === 'overview' && (
        <div className="rb-grid rb-grid--write">
          <aside className="rb-aside rb-aside--outline">{outlineSection}</aside>
          <main className="rb-main rb-main--article">{articleSection}</main>
        </div>
      )}

      {workspaceMode === 'outlining' && (
        <div className="rb-solo-pane rb-solo-outline">
          {isHuman && angleResteerCard}
          {outlineSection}
        </div>
      )}

      {workspaceMode === 'writing' && (
        <div className="rb-writing-split rb-solo-pane rb-solo-writing">
          <div className="rb-writing-split-main">
            <main className="rb-main rb-main--article">{articleSection}</main>
          </div>
          <aside className="rb-writing-split-aside" aria-label="Research highlights checklist">
            <section className="rb-card rb-highlights-checklist-card">
              <h3 className="rb-highlights-checklist-title">Research Highlights</h3>
              <p className="muted sm rb-highlights-checklist-hint">
                Check off as you cover each point. Edit highlights in Overview, Research, or
                Outlining.
              </p>
              <div className="rb-highlights-checklist-body">
                {claims.length === 0 ? (
                  <p className="muted sm">No research highlights yet.</p>
                ) : (
                  <ul className="rb-highlights-checklist-list">
                    {sortedClaimsForWritingChecklist.map((c) => {
                      const used = !!highlightUsedIds[c.id];
                      const srcNum = c.source_id
                        ? sourceNumById.get(c.source_id)
                        : null;
                      return (
                        <li
                          key={c.id}
                          className={`rb-highlight-check-card${used ? ' rb-highlight-check-card--done' : ''}`}
                        >
                          <label className="rb-highlight-check-label">
                            <input
                              type="checkbox"
                              checked={used}
                              onChange={() => toggleHighlightUsed(c.id)}
                              className="rb-highlight-check-input"
                            />
                            <span className="rb-highlight-check-main">
                              <span className="rb-highlight-check-text">{c.claim}</span>
                              <span className="rb-highlight-check-src">
                                {srcNum != null ? `Src ${srcNum}` : '—'}
                              </span>
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </section>
          </aside>
        </div>
      )}

      <div
        className="rb-research-strip"
        aria-label="Research packet: sources and research highlights"
      >
        {workspaceMode === 'writing' ? (
          <>
            {sourcesSection}
            {rb.generated_at && (
              <p className="gen-meta muted rb-strip-footer">
                Packet: {new Date(rb.generated_at).toLocaleString()}
                {rb.serp_query && ` · ${rb.serp_query}`}
              </p>
            )}
          </>
        ) : (
          <>
            {workspaceMode === 'outlining' ? (
              <>
                {researchHighlightsSection}
                {sourcesSection}
              </>
            ) : workspaceMode === 'research' ? (
              <>
                <div className="rb-research-sources-queries-split">
                  <div className="rb-research-sources-queries-main">{sourcesSection}</div>
                  <aside
                    className="rb-card rb-research-queries-aside"
                    aria-label="Open target keyword searches in Google"
                  >
                    <h3 className="rb-research-queries-title">Keyword search</h3>
                    <p className="muted sm rb-research-queries-hint">
                      Phrases from the content queue <strong>target keyword</strong> field
                      (comma-separated list). Each link opens Google for that phrase only.
                    </p>
                    {researchGoogleQueries.length === 0 ? (
                      <p className="muted sm">
                        No target keyword on this queue item. Add one from the Content queue, then
                        refresh or reopen this draft.
                      </p>
                    ) : (
                      <ul className="rb-research-queries-list">
                        {researchGoogleQueries.map((item, idx) => (
                          <li key={`${item.query}:${idx}`}>
                            <a
                              href={googleSearchUrl(item.query)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="rb-research-query-link"
                            >
                              {item.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </aside>
                </div>
                {researchHighlightsSection}
              </>
            ) : (
              <>
                {sourcesSection}
                {researchHighlightsSection}
              </>
            )}
            {rb.generated_at && (
              <p className="gen-meta muted rb-strip-footer">
                Packet: {new Date(rb.generated_at).toLocaleString()}
                {rb.serp_query && ` · ${rb.serp_query}`}
              </p>
            )}
          </>
        )}
      </div>

      {execSummaryModalOpen && (
        <div
          className="rb-modal-backdrop"
          role="presentation"
          onClick={() => setExecSummaryModalOpen(false)}
        >
          <div
            className="rb-modal-panel rb-modal-panel--exec-summary"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rb-exec-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rb-modal-head">
              <h4 id="rb-exec-modal-title">Executive summary</h4>
              <button
                type="button"
                className="rb-modal-close"
                aria-label="Close"
                onClick={() => setExecSummaryModalOpen(false)}
              >
                ×
              </button>
            </div>
            <textarea
              id="rb-exec-modal-textarea"
              className="rb-textarea rb-exec-summary-modal-field"
              value={execSummaryDraft}
              onChange={(e) => setExecSummaryDraft(e.target.value)}
              rows={12}
              placeholder="Strategic summary for this piece…"
            />
            <div className="rb-modal-actions rb-modal-actions--row">
              <button
                type="button"
                className="btn-secondary-sm"
                onClick={() => setExecSummaryModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary-sm"
                onClick={() => {
                  setExecutiveSummary(execSummaryDraft);
                  setExecSummaryModalOpen(false);
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {modalSource && (
        <div
          className="rb-modal-backdrop"
          role="presentation"
          onClick={() => setSourceModalId(null)}
        >
          <div
            className="rb-modal-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rb-source-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rb-modal-head">
              <h4 id="rb-source-modal-title">Source options</h4>
              <button
                type="button"
                className="rb-modal-close"
                aria-label="Close"
                onClick={() => setSourceModalId(null)}
              >
                ×
              </button>
            </div>
            <a
              className="rb-modal-link"
              href={modalSource.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {modalSource.title || modalSource.url}
            </a>
            {modalSource.snippet && (
              <p className="rb-modal-snippet">{modalSource.snippet}</p>
            )}
            <div className="rb-modal-fields">
              <div>
                <label className="rb-mini-label" htmlFor="rb-src-influence">
                  Source role
                </label>
                <select
                  id="rb-src-influence"
                  className="rb-select"
                  value={modalSource.influence || 'supporting'}
                  disabled={sourceBusy === modalSource.id}
                  onChange={(e) => {
                    const v = e.target.value;
                    void updateSourceFields(modalSource.id, {
                      influence: v || 'supporting',
                    });
                  }}
                >
                  <option value="preferred">Preferred</option>
                  <option value="supporting">Supporting</option>
                  <option value="counterpoint">Counterpoint</option>
                  <option value="statistical_authority">Statistical authority</option>
                  <option value="context_only">Context only</option>
                </select>
              </div>
              <div>
                <label className="rb-mini-label" htmlFor="rb-src-review">
                  Review
                </label>
                <select
                  id="rb-src-review"
                  className="rb-select"
                  value={modalSource.review_status || 'needs_review'}
                  disabled={sourceBusy === modalSource.id}
                  onChange={(e) => {
                    const v = e.target.value;
                    void updateSourceFields(modalSource.id, {
                      review_status: v || 'needs_review',
                    });
                  }}
                >
                  <option value="needs_review">Needs review</option>
                  <option value="verified">Verified</option>
                </select>
              </div>
            </div>
            <div className="rb-modal-actions">
              <button
                type="button"
                className="rb-btn-reject-source rb-btn-reject-modal"
                title="Remove from list and exclude this URL on refresh"
                disabled={sourceBusy === modalSource.id}
                onClick={() => void rejectSource(modalSource)}
              >
                {sourceBusy === modalSource.id ? 'Removing…' : 'Reject source'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalClaim && (
        <div
          className="rb-modal-backdrop"
          role="presentation"
          onClick={() => setClaimModalId(null)}
        >
          <div
            className="rb-modal-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rb-claim-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rb-modal-head">
              <h4 id="rb-claim-modal-title">Research highlight</h4>
              <button
                type="button"
                className="rb-modal-close"
                aria-label="Close"
                onClick={() => setClaimModalId(null)}
              >
                ×
              </button>
            </div>
            <label className="rb-mini-label" htmlFor="rb-claim-body">
              Highlight text
            </label>
            <textarea
              id="rb-claim-body"
              className="rb-textarea rb-claim-modal-textarea"
              rows={4}
              value={claimModalBody}
              onChange={(e) => setClaimModalBody(e.target.value)}
              disabled={claimBusy === modalClaim.id}
            />
            <button
              type="button"
              className="btn-primary-sm rb-claim-save-text"
              disabled={claimBusy === modalClaim.id}
              onClick={() => {
                const t = claimModalBody.trim();
                if (!t) {
                  showToast('Highlight text cannot be empty.', 'warning');
                  return;
                }
                void updateClaimFields(modalClaim.id, { claim: t });
              }}
            >
              Save text
            </button>
            <div className="rb-modal-fields rb-claim-source-row">
              <div className="rb-claim-source-full">
                <label className="rb-mini-label" htmlFor="rb-claim-src">
                  Supporting source
                </label>
                <select
                  id="rb-claim-src"
                  className="rb-select"
                  value={modalClaim.source_id || ''}
                  disabled={claimBusy === modalClaim.id}
                  onChange={(e) => {
                    const v = e.target.value;
                    void updateClaimFields(modalClaim.id, {
                      source_id: v === '' ? null : v,
                    });
                  }}
                >
                  <option value="">None — pick a source</option>
                  {sources.map((s) => (
                    <option key={s.id || s.url} value={s.id}>
                      {(s.title || s.url || '').slice(0, 72)}
                      {s.id ? ` (${s.id})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="rb-modal-actions rb-claim-modal-actions">
              <button
                type="button"
                className="btn-secondary-sm"
                disabled={claimBusy === modalClaim.id || modalClaim.approved}
                onClick={() =>
                  void updateClaimFields(modalClaim.id, {
                    approved: true,
                    warning: null,
                  })
                }
              >
                Approve highlight
              </button>
              <button
                type="button"
                className="rb-btn-reject-source rb-btn-reject-modal"
                disabled={claimBusy === modalClaim.id}
                onClick={() => void removeClaim(modalClaim.id)}
              >
                {claimBusy === modalClaim.id ? 'Removing…' : 'Reject & remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {warningModalClaim && (
        <div
          className="rb-modal-backdrop"
          role="presentation"
          onClick={() => setWarningModalClaimId(null)}
        >
          <div
            className="rb-modal-panel rb-modal-panel--claim-warn"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rb-warn-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rb-modal-head">
              <h4 id="rb-warn-modal-title">Highlight warning</h4>
              <button
                type="button"
                className="rb-modal-close"
                aria-label="Close"
                onClick={() => setWarningModalClaimId(null)}
              >
                ×
              </button>
            </div>
            <p className="rb-warn-modal-claim muted sm">
              {(warningModalClaim.claim || '').slice(0, 200)}
              {(warningModalClaim.claim || '').length > 200 ? '…' : ''}
            </p>
            <p className="rb-warn-modal-body">
              {claimAlertMessage(warningModalClaim) ||
                'No active warning for this highlight.'}
            </p>
            <div className="rb-modal-actions rb-warn-modal-actions">
              <button
                type="button"
                className="btn-secondary-sm"
                onClick={() => setWarningModalClaimId(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {snapshotModalOpen && (
        <div
          className="rb-modal-backdrop"
          role="presentation"
          onClick={(e) =>
            e.target === e.currentTarget && !savingSnapshot && setSnapshotModalOpen(false)
          }
        >
          <div
            className="rb-modal-panel rb-modal-panel--snapshot"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rb-snapshot-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rb-modal-head">
              <h4 id="rb-snapshot-modal-title">Save snapshot</h4>
              <button
                type="button"
                className="rb-modal-close"
                disabled={savingSnapshot}
                onClick={() => setSnapshotModalOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="rb-snapshot-hint muted">
              Creates a named revision you can browse or restore later. Your draft still autosaves in the background.
            </p>
            <label className="rb-snapshot-label" htmlFor="rb-snapshot-desc">
              Description <span className="muted">(optional)</span>
            </label>
            <input
              id="rb-snapshot-desc"
              type="text"
              className="rb-snapshot-input"
              placeholder="e.g. rewrote intro, editor revisions"
              value={snapshotDescription}
              onChange={(e) => setSnapshotDescription(e.target.value)}
              maxLength={500}
              disabled={savingSnapshot}
            />
            <label className="rb-snapshot-check">
              <input
                type="checkbox"
                checked={snapshotIncludeResearch}
                onChange={(e) => setSnapshotIncludeResearch(e.target.checked)}
                disabled={savingSnapshot}
              />
              Include research packet (sources, claims, and related data)
            </label>
            <div className="rb-modal-actions rb-modal-actions--row">
              <button
                type="button"
                className="btn-secondary-sm"
                disabled={savingSnapshot}
                onClick={() => setSnapshotModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary-sm"
                disabled={savingSnapshot}
                onClick={() => void handleConfirmSaveSnapshot()}
              >
                {savingSnapshot ? 'Saving…' : 'Save snapshot'}
              </button>
            </div>
          </div>
        </div>
      )}

      <DraftRevisionsModal
        draftId={draftId}
        open={revisionsModalOpen}
        onClose={() => setRevisionsModalOpen(false)}
        onRestored={async () => {
          await load();
        }}
      />
    </div>
  );
}

export default ResearchBriefWorkspace;
